/**
 * Universal Product Card Actions Handler
 * Handles add to cart, quantity controls (+/-), and cart synchronization
 * Works with card-product-hits component
 */
(function () {
if (window.ProductCardActions) {
  if (typeof window.initProductCardActions === 'function') {
    window.initProductCardActions(document);
  }
  return;
}

class ProductCardActions {
  constructor(container = document) {
    this.container = container;
    this.cartState = {};
    this.headerCartTotalValueEl = document.querySelector('.cart-total-price .totals__total-value');
    this.headerCartTotalValueClassName = this.headerCartTotalValueEl?.className || null;
    this.headerCartTotalValueExampleText = this.headerCartTotalValueEl?.textContent?.trim() || null;
    
    // Cart data cache to reduce fetch requests
    this.cartDataCache = null;
    this.cartDataCacheTime = 0;
    this.CART_CACHE_DURATION = 1500; // 1.5 seconds cache
    this.cartSyncTimer = null;
    
    this.init();
  }

  init() {
    this.initializeCart();
    this.setupEventListeners();
    this.setupPubSubListeners();
    this.setupPageVisibilityHandlers();
  }
  
  setupPageVisibilityHandlers() {
    // Синхронизация при возврате на страницу (кнопка "назад" браузера)
    window.addEventListener('pageshow', (event) => {
      // event.persisted = true означает, что страница загружена из bfcache (back-forward cache)
      if (event.persisted) {
        console.log('[ProductCardActions] Page restored from cache, syncing...');
        this.cartDataCache = null; // Сбрасываем кеш
        this.syncCardsWithCart();
      }
    });
    
    // Синхронизация когда вкладка становится видимой
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        console.log('[ProductCardActions] Tab became visible, syncing...');
        this.cartDataCache = null; // Сбрасываем кеш
        this.syncCardsWithCart();
      }
    });
  }

  /**
   * Вычисляет total price минус стоимость упаковки
   */
  getTotalWithoutPackaging(cart) {
    if (!cart || !cart.items) return 0;

    let packagingCost = 0;
    
    // Получаем ID упаковок из конфига
    if (window.PackagingUtils) {
      const config = window.PackagingUtils.getConfig();
      if (config && config.frozenId && config.cooledId) {
        const frozenId = String(config.frozenId);
        const cooledId = String(config.cooledId);
        
        cart.items.forEach(item => {
          const variantId = String(item.variant_id || item.id);
          if (variantId === frozenId || variantId === cooledId) {
            packagingCost += item.final_line_price || 0;
          }
        });
      }
    }

    return cart.total_price - packagingCost;
  }

  formatCartTotalPrice(totalPriceCents) {
    const locale = document.documentElement.lang || 'de';
    const numeric = (totalPriceCents / 100).toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const example = this.headerCartTotalValueExampleText || '';
    const symbolMatch = example.match(/[^\d\s.,]+/);
    const symbol = symbolMatch ? symbolMatch[0] : '€';
    const isPrefix = example.trim().startsWith(symbol);

    return isPrefix ? `${symbol}${numeric}` : `${numeric} ${symbol}`.trim();
  }

  updateCartSectionsWithoutOpen(parsedState) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || !parsedState || !parsedState.sections) return;

    if (window.frostixCartDrawerPatch?.renderSections?.(parsedState)) {
      return;
    }

    cartDrawer.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const html = parsedState.sections[section.id];
      if (!html) return;

      sectionElement.innerHTML = cartDrawer.getSectionInnerHTML(html, section.selector);
    });

    const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', cartDrawer.close.bind(cartDrawer));
  }

  async updateCartHeader(cartData = null) {
    try {
      // Use provided cartData or fetch from cache/API
      const cart = cartData || await this.fetchCartData();
      this.updateCartHeaderUI(cart);
    } catch (error) {
      // Silent error handling
    }
  }

  scheduleCartSync(delay = 8000) {
    window.clearTimeout(this.cartSyncTimer);
    this.cartSyncTimer = window.setTimeout(() => {
      this.syncCardsWithCart();
    }, delay);
  }

  updateCartHeaderUI(cart) {
    document.querySelectorAll('.cart-count-bubble span, [data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
    });
    
    // Вычисляем total без упаковки
    const totalWithoutPackaging = this.getTotalWithoutPackaging(cart);
    
    // Header money is synchronized with the drawer footer in cart-drawer-ui.js.
    // A displayCart-only rewrite here would drop server cart-level discounts.
    // Обновляем все элементы с data-bottom-cart-total (мобильная корзина)
    document.querySelectorAll('[data-bottom-cart-total]').forEach(el => {
      el.textContent = this.formatCartTotalPrice(totalWithoutPackaging);
    });
    
    // Обновляем иконки корзины (пустая/полная) в мобильной корзине
    const isEmpty = !cart || cart.item_count === 0;
    document.querySelectorAll('[data-cart-icon-empty]').forEach(icon => {
      if (isEmpty) {
        icon.removeAttribute('hidden');
      } else {
        icon.setAttribute('hidden', '');
      }
    });
    document.querySelectorAll('[data-cart-icon-full]').forEach(icon => {
      if (isEmpty) {
        icon.setAttribute('hidden', '');
      } else {
        icon.removeAttribute('hidden');
      }
    });
    
    // Обновляем бэйдж в мобильной корзине
    document.querySelectorAll('[data-bottom-cart-badge]').forEach(badge => {
      if (isEmpty) {
        badge.setAttribute('hidden', '');
      } else {
        badge.removeAttribute('hidden');
        badge.textContent = cart.item_count;
      }
    });
    
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true
    }));
  }

  applyCartToCards(cart) {
    if (!cart || !Array.isArray(cart.items)) return;

    this.cartDataCache = cart;
    this.cartDataCacheTime = Date.now();

    // Полностью сбрасываем cartState
    this.cartState = {};

    const variantsByProduct = {};
    cart.items.forEach(item => {
      const productId = item.product_id?.toString();
      const variantId = item.variant_id?.toString();
      if (!productId || !variantId) return;
      if (!variantsByProduct[productId]) variantsByProduct[productId] = [];
      variantsByProduct[productId].push({ variantId, quantity: item.quantity });
    });

    Object.keys(variantsByProduct).forEach((productId) => {
      const totalQty = variantsByProduct[productId].reduce((sum, v) => sum + v.quantity, 0);
      this.cartState[productId] = totalQty;
    });

    this.container.querySelectorAll('.hits-add-form').forEach(form => {
      const addButton = form.querySelector('.hits-add-btn-icon');
      const productId = addButton?.dataset.productId;
      if (!productId) return;

      const qtyControls = form.querySelector('.hits-quantity-controls');
      const qtyDisplay = form.querySelector('.hits-qty-display');
      const currentActiveVariant = form.dataset.activeVariantId;
      const keepPriceVisible = form.dataset.collectionCard === 'true';

      if (variantsByProduct[productId] && variantsByProduct[productId].length > 0) {
        const match = currentActiveVariant
          ? variantsByProduct[productId].find(v => v.variantId === currentActiveVariant)
          : null;
        form.dataset.activeVariantId = (match ? match.variantId : variantsByProduct[productId][0].variantId);
        const input = form.querySelector('input[name="id"]');
        if (input) input.value = form.dataset.activeVariantId;
      } else {
        delete form.dataset.activeVariantId;
      }

      const priceDisplay = form.querySelector('.hits-add-wrapper__price');

      if (this.cartState[productId] > 0) {
        if (addButton) addButton.style.display = 'none';
        if (priceDisplay) priceDisplay.style.display = keepPriceVisible ? '' : 'none';
        if (qtyControls) qtyControls.style.display = 'flex';
        if (qtyDisplay) qtyDisplay.textContent = this.cartState[productId];
      } else {
        if (addButton) addButton.style.display = 'flex';
        if (priceDisplay) priceDisplay.style.display = '';
        if (qtyControls) qtyControls.style.display = 'none';
      }
    });
  }

  async fetchCartData(useCache = true) {
    // Единый источник правды: берём корзину из CartStore, без своего /cart.js.
    if (window.CartStore) {
      const cart = useCache ? window.CartStore.getCart() : await window.CartStore.refresh();
      if (cart && Array.isArray(cart.items)) {
        this.cartDataCache = cart;
        this.cartDataCacheTime = Date.now();
        return cart;
      }
    }

    if (useCache) {
      const now = Date.now();
      if (this.cartDataCache && (now - this.cartDataCacheTime) < this.CART_CACHE_DURATION) {
        return this.cartDataCache;
      }
    }

    const response = await fetch('/cart.js');
    const cart = await response.json();

    this.cartDataCache = cart;
    this.cartDataCacheTime = Date.now();

    return cart;
  }

  isRateLimited() {
    return !!window.FrostixCartService?.isRateLimited?.();
  }

  async initializeCart() {
    // Берём начальное состояние из единого источника (CartStore), не делая свой /cart.js.
    if (window.CartStore) {
      try {
        const cart = await window.CartStore.ready();
        if (cart && Array.isArray(cart.items)) {
          this.applyCartToCards(cart);
          this.updateCartHeaderUI(cart);
        }
      } catch (e) {
        // sync произойдёт по cart:changed
      }
      return;
    }
    try {
      const cart = await this.fetchCartData(false); // фолбэк, если стор недоступен
      this.applyCartToCards(cart);
    } catch (error) {
      // Silent error - cart will sync on first interaction
    }
  }

  setupPubSubListeners() {
    // Единый источник правды: CartStore эмитит cart:changed после каждой мутации.
    // Карточки и счётчики синхронизируем отсюда — никаких локальных инкрементов.
    document.addEventListener('cart:changed', (event) => {
      const cart = event && event.detail && event.detail.cart;
      if (cart && Array.isArray(cart.items)) {
        this.applyCartToCards(cart);
        this.updateCartHeaderUI(cart);
      }
    });

    // Карточки/счётчики обновляются ТОЛЬКО по cart:changed (единый источник).
    // Старый PUB_SUB cartUpdate здесь больше не обрабатываем — он задваивал
    // количество (markProductAdded) и слал лишние /cart.js (scheduleCartSync).
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        this.handleVariantChange(event);
      });
    }
  }

  handleVariantChange(event) {
    const sectionId = event.data?.sectionId;
    const variant = event.data?.variant;
    if (!sectionId || !variant || !variant.id || variant.price === undefined || variant.price === null) return;

    const esc = window.CSS && typeof window.CSS.escape === 'function' ? window.CSS.escape : (s) => String(s).replace(/"/g, '\\"');
    const productInfoEl = document.querySelector(`product-info[data-section="${esc(sectionId)}"]`);

    const quickAddModal = productInfoEl?.closest('quick-add-modal');
    const modalId = quickAddModal?.id || '';
    const productId = quickAddModal?.dataset.productId || modalId.match(/^QuickAdd-(\d+)/)?.[1] || null;
    if (!productId) return;

    const wrapper = quickAddModal?.closest('.hits-add-wrapper')
      || this.container.querySelector(`.hits-add-wrapper[data-product-id="${productId}"]`);
    if (!wrapper) return;

    const form = wrapper.closest('form');
    const priceDisplay = form?.querySelector('[data-price-display]');
    if (priceDisplay) {
      const pricePrefix = priceDisplay.querySelector('.price-prefix');
      if (pricePrefix) pricePrefix.remove();
      priceDisplay.classList.remove('has-prefix', 'has-old-price');

      const currentEl = priceDisplay.querySelector('[data-price-current]');
      if (currentEl) {
        currentEl.textContent = this.formatCartTotalPrice(variant.price);
      }

      const compareAt = variant.compare_at_price;
      const hasDiscount = compareAt && Number(compareAt) > Number(variant.price);
      let oldEl = priceDisplay.querySelector('[data-price-old]');
      if (hasDiscount) {
        priceDisplay.classList.add('has-old-price');
        if (!oldEl) {
          oldEl = document.createElement('span');
          oldEl.className = 'hits-price-old t3';
          oldEl.setAttribute('data-price-old', '');
          priceDisplay.appendChild(oldEl);
        }
        oldEl.textContent = this.formatCartTotalPrice(compareAt);
      } else if (oldEl) {
        oldEl.remove();
      }
    }

    if (form) {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) variantInput.value = variant.id;
      form.dataset.activeVariantId = variant.id.toString();
    }
  }

  async syncCardsWithCart() {
    try {
      // Принудительно запрашиваем свежие данные корзины (без кеша)
      const cart = await this.fetchCartData(false);
      this.applyCartToCards(cart);
    } catch (error) {
      // Silent error - will sync on next cart update
    }
  }

  markProductAdded(productId, variantId, quantity = 1) {
    if (!productId) return;

    const productKey = String(productId);
    this.cartState[productKey] = Number(this.cartState[productKey] || 0) + quantity;

    const form = [...this.container.querySelectorAll('.hits-add-form')].find((candidate) => {
      const btn = candidate.querySelector('.hits-add-btn-icon');
      return btn?.dataset.productId === productKey;
    });

    if (!form) return;

    if (variantId) {
      form.dataset.activeVariantId = String(variantId);
      const input = form.querySelector('input[name="id"]');
      if (input) input.value = String(variantId);
    }

    this.updateUI(form, productKey);
  }

  setupEventListeners() {
    this.container.querySelectorAll('.hits-add-form').forEach(form => {
      if (form.dataset.productCardActionsBound === 'true') return;
      form.dataset.productCardActionsBound = 'true';

      const addButton = form.querySelector('.hits-add-btn-icon');
      const qtyControls = form.querySelector('.hits-quantity-controls');
      const qtyDisplay = form.querySelector('.hits-qty-display');
      const minusBtn = form.querySelector('[data-action="decrease"]');
      const plusBtn = form.querySelector('[data-action="increase"]');
      const productId = addButton?.dataset.productId;

      if (!productId) return;

      this.cartState[productId] = this.cartState[productId] || 0;

      const getVariantId = () => {
        const activeVariantId = form.dataset.activeVariantId;
        if (activeVariantId) return activeVariantId;
        const input = form.querySelector('input[name="id"]');
        return input ? input.value : addButton?.dataset.variantId;
      };

      const isQuickAddOpener = addButton?.dataset.openQuickAdd === 'true';

      const cardMeta = () => ({
        product_id: productId,
        product_type: addButton.dataset.productType || '',
        requires_shipping: addButton.dataset.requiresShipping !== 'false',
        price: parseInt(addButton.dataset.price, 10) || 0,
        compare_at_price: parseInt(addButton.dataset.compareAtPrice, 10) || 0,
      });

      // Текущее количество КОНКРЕТНОГО варианта из единого источника (displayCart).
      const cardVariantQty = (vid) => {
        const c = window.CartStore && window.CartStore.getCart();
        if (c && Array.isArray(c.items)) {
          const line = c.items.find(
            (it) => String(it.variant_id != null ? it.variant_id : it.id) === String(vid)
          );
          if (line) return line.quantity;
        }
        return this.cartState[productId] || 0;
      };

      addButton?.addEventListener('click', (e) => {
        e.preventDefault();
        if (isQuickAddOpener) return;

        const variantId = getVariantId();
        if (!variantId || !window.CartStore) return;

        // Оптимистично: меняем pending + мгновенный UI (cart:changed).
        // Реальный запрос уйдёт ПАЧКОЙ позже (debounce / открытие корзины /
        // checkout / уход со страницы). Никакой сети на клик — не выбиваем 429.
        window.CartStore.addItems([
          Object.assign({ id: parseInt(variantId, 10), quantity: 1 }, cardMeta()),
        ], { immediate: true });

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.openAfterAdd === 'function') {
          cartDrawer.openAfterAdd(addButton);
        }
      });

      plusBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = getVariantId();
        if (!variantId || !window.CartStore) return;
        window.CartStore.setQty(variantId, cardVariantQty(variantId) + 1, cardMeta());
      });

      minusBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = getVariantId();
        if (!variantId || !window.CartStore) return;
        const cur = cardVariantQty(variantId);
        if (cur > 0) window.CartStore.setQty(variantId, cur - 1, cardMeta());
      });
    });
  }

  updateUI(form, productId) {
    const addButton = form.querySelector('.hits-add-btn-icon');
    const qtyControls = form.querySelector('.hits-quantity-controls');
    const qtyDisplay = form.querySelector('.hits-qty-display');
    const priceDisplay = form.querySelector('.hits-add-wrapper__price');
    const keepPriceVisible = form.dataset.collectionCard === 'true';

    if (this.cartState[productId] > 0) {
      // Товар в корзине - скрываем цену и кнопку +, показываем контролы количества
      if (addButton) addButton.style.display = 'none';
      if (priceDisplay) priceDisplay.style.display = keepPriceVisible ? '' : 'none';
      if (qtyControls) qtyControls.style.display = 'flex';
      if (qtyDisplay) qtyDisplay.textContent = this.cartState[productId];
    } else {
      // Товара нет в корзине - показываем цену и кнопку +, скрываем контролы
      if (addButton) addButton.style.display = 'flex';
      if (priceDisplay) priceDisplay.style.display = '';
      if (qtyControls) qtyControls.style.display = 'none';
    }
  }
}

function initProductCardActions(container = document) {
  window.productCardActionsInstances = window.productCardActionsInstances || new WeakMap();
  if (window.productCardActionsInstances.has(container)) {
    const instance = window.productCardActionsInstances.get(container);
    instance.setupEventListeners();
    return instance;
  }

  const instance = new ProductCardActions(container);
  window.productCardActionsInstances.set(container, instance);
  return instance;
}

// Auto-initialize for entire document
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initProductCardActions(document);
  });
} else {
  initProductCardActions(document);
}

// Export for manual initialization if needed
window.ProductCardActions = ProductCardActions;
window.initProductCardActions = initProductCardActions;
})();
